// BUILD-042 — HR Administration > Company setup (Owner decision 2026-10-24).
// A discoverable landing over the EXISTING company-wide HR setup destinations. It adds no route, no permission and no data: every card links to a
// destination that already exists, at its unchanged route. Permission awareness is advisory UX only - the server re-checks every request:
//   * a card whose governing capability the caller is KNOWN to lack (GET /hr/me surfaces flag === false) is shown disabled with a one-line
//     "Requires the '<label>' permission" note, instead of leading to a screen that would refuse;
//   * an UNKNOWN flag (an older server, or access not resolved) is not hidden or disabled - it opens normally and the server decides.
// Training catalog and Time-off policies remain visibly Planned (they open the existing planned panels).
import { Link } from 'react-router-dom';
import { useHrAccess } from '../HrAccessContext';
import { COMPANY_SETUP_ITEMS, adminSubtitleFor, type AdminNavItem } from './adminNavGroups';
import { PERMISSION_LABEL } from './loadState';

const REQUIRED_LABEL: Record<NonNullable<AdminNavItem['requires']>, string> = {
  mayManageChecklistTemplates: PERMISSION_LABEL.checklistTemplates,
  mayManageDocuments: PERMISSION_LABEL.documents,
  mayManageBenefits: 'Manage benefits',
};

export function CompanySetupPanel() {
  const { state } = useHrAccess();
  const surfaces = state.kind === 'ready' ? state.me.surfaces : null;

  return (
    <div data-testid="company-setup-panel">
      <div className="mb-5">
        <div className="text-[11px] font-semibold tracking-wider uppercase text-gray-500">HR Administration</div>
        <h1 className="text-2xl font-bold text-gray-900 leading-tight mt-0.5">Company setup</h1>
        <p className="text-sm text-gray-500 mt-1">{adminSubtitleFor('companySetup')}</p>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
        {COMPANY_SETUP_ITEMS.map((item) => {
          const flag = item.requires ? surfaces?.[item.requires] : undefined;
          const blocked = !!item.requires && flag === false;
          const body = (
            <>
              <div className="flex items-start justify-between gap-2">
                <span className={`text-sm font-semibold break-words min-w-0 ${blocked ? 'text-gray-500' : 'text-gray-900'}`}>{item.label}</span>
                {item.planned && <span className="text-[11px] text-gray-500 border border-gray-200 rounded px-1.5 py-0.5 flex-shrink-0">Planned</span>}
              </div>
              {item.blurb && <p className="text-xs text-gray-500 mt-1 break-words">{item.blurb}</p>}
              {blocked
                ? <p className="text-xs text-gray-600 mt-2" data-testid={`company-setup-needs-${item.key}`}>Requires the “{REQUIRED_LABEL[item.requires!]}” permission</p>
                : <span className="inline-block text-xs text-[#176A9B] mt-2">{item.planned ? 'See the planned setup →' : 'Open →'}</span>}
            </>
          );
          return (
            <li key={item.key} className="min-w-0">
              {blocked ? (
                <div aria-disabled="true" data-testid={`company-setup-card-${item.key}`} data-blocked="true" className="h-full bg-gray-50 rounded-lg border border-gray-200 p-4 opacity-90">{body}</div>
              ) : (
                <Link to={item.path} data-testid={`company-setup-card-${item.key}`} className="block h-full bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:border-[#4AABDE] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4AABDE]">{body}</Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
